import { useState, useEffect, useRef } from 'react';
import { createPortal } from 'react-dom';
import { FaEdit as NotepadIcon, FaPlus, FaTimes, FaEdit, FaEye, FaCode } from 'react-icons/fa';
import { useBaseComponentFullscreen } from './BaseComponent';

export default function NotepadComponent({ component, onUpdate }) {
  const isFullscreen = useBaseComponentFullscreen();
  const data = component.data || { tabs: [], active_tab: 0 };
  const [editingTabIndex, setEditingTabIndex] = useState(null);
  const [editTabName, setEditTabName] = useState('');
  const [deleteConfirm, setDeleteConfirm] = useState(null);
  const [localContent, setLocalContent] = useState(data.tabs[data.active_tab]?.content || '');
  const [cursorPos, setCursorPos] = useState(0);
  const [isMarkdownMode, setIsMarkdownMode] = useState(false);
  const textareaRef = useRef(null);
  const debounceTimer = useRef(null);

  // Sync local content with active tab
  useEffect(() => {
    setLocalContent(data.tabs[data.active_tab]?.content || '');
  }, [data.active_tab, data.tabs]);

  // Restore cursor position after update
  useEffect(() => {
    if (textareaRef.current) {
      textareaRef.current.selectionStart = cursorPos;
      textareaRef.current.selectionEnd = cursorPos;
    }
  }, [localContent, cursorPos]);

  const addTab = () => {
    const newTabs = [
      ...data.tabs,
      {
        name: `Tab ${data.tabs.length + 1}`,
        content: "",
        created_at: new Date().toISOString()
      }
    ];
    onUpdate({
      ...component,
      data: {
        ...data,
        tabs: newTabs,
        active_tab: newTabs.length - 1
      }
    });
  };

  const updateTabContent = (content, cursorPosition) => {
    setLocalContent(content);
    setCursorPos(cursorPosition);

    // Clear any pending debounce
    if (debounceTimer.current) {
      clearTimeout(debounceTimer.current);
    }

    // Set new debounce
    debounceTimer.current = setTimeout(() => {
      const updatedTabs = [...data.tabs];
      updatedTabs[data.active_tab].content = content;
      onUpdate({
        ...component,
        data: {
          ...data,
          tabs: updatedTabs
        }
      });
    }, 1000); // 1 second debounce
  };

  const deleteTab = (index) => {
    const newTabs = data.tabs.filter((_, i) => i !== index);
    onUpdate({
      ...component,
      data: {
        ...data,
        tabs: newTabs,
        active_tab: Math.min(data.active_tab, newTabs.length - 1)
      }
    });
    setDeleteConfirm(null);
  };

  const startEditingTab = (index) => {
    setEditingTabIndex(index);
    setEditTabName(data.tabs[index].name);
  };

  const saveTabName = () => {
    if (editTabName.trim()) {
      const updatedTabs = [...data.tabs];
      updatedTabs[editingTabIndex].name = editTabName.trim();
      onUpdate({
        ...component,
        data: {
          ...data,
          tabs: updatedTabs
        }
      });
      setEditingTabIndex(null);
    }
  };

  const handleTextChange = (e) => {
    const { value, selectionStart } = e.target;
    updateTabContent(value, selectionStart);
  };

  // Simple markdown parser
  const parseMarkdown = (text) => {
    if (!text) return '';
    
    return text
      // Escape HTML first
      .replace(/&/g, '&amp;')
      .replace(/</g, '&lt;')
      .replace(/>/g, '&gt;')
      // Code blocks (must be before other replacements)
      .replace(/```([\s\S]*?)```/g, '<pre class="bg-[var(--bg-secondary)] p-3 rounded mt-2 mb-2 overflow-x-auto border"><code>$1</code></pre>')
      // Headers (must handle line start properly)
      .replace(/^### (.*)$/gm, '<h3 class="text-lg font-semibold mt-4 mb-2 text-[var(--text-primary)]">$1</h3>')
      .replace(/^## (.*)$/gm, '<h2 class="text-xl font-semibold mt-4 mb-2 text-[var(--text-primary)]">$1</h2>')
      .replace(/^# (.*)$/gm, '<h1 class="text-2xl font-bold mt-4 mb-3 text-[var(--text-primary)]">$1</h1>')
      // Bold
      .replace(/\*\*(.*?)\*\*/g, '<strong class="font-semibold">$1</strong>')
      .replace(/__(.*?)__/g, '<strong class="font-semibold">$1</strong>')
      // Italic
      .replace(/\*(.*?)\*/g, '<em class="italic">$1</em>')
      .replace(/_(.*?)_/g, '<em class="italic">$1</em>')
      // Code inline
      .replace(/`(.*?)`/g, '<code class="bg-[var(--bg-secondary)] px-1 py-0.5 rounded text-sm font-mono">$1</code>')
      // Links
      .replace(/\[([^\]]+)\]\(([^)]+)\)/g, '<a href="$2" target="_blank" rel="noopener noreferrer" class="text-[var(--primary)] underline hover:opacity-80">$1</a>')
      // Line breaks and paragraphs
      .replace(/\n\n/g, '</p><p class="mb-2">')
      .replace(/\n/g, '<br>')
      // Wrap in paragraph if not already wrapped
      .replace(/^(?!<[h1-6|pre|div])/, '<p class="mb-2">')
      .replace(/(?<!>)$/, '</p>');
  };

  return (
    <>
      <div className="tab-bar flex items-center gap-1 mb-4">
        <div className="flex flex-wrap items-center gap-1 flex-1 min-w-0">
          {data.tabs.map((tab, index) => (
            <div
              key={index}
              className={`flex items-center gap-1 px-2 py-1 rounded-md cursor-pointer transition-colors text-sm ${
                index === data.active_tab 
                  ? 'bg-[var(--primary)] text-white'
                  : 'bg-[var(--bg-hover)] hover:bg-[var(--bg-hover)] text-[var(--text-primary)]'
              }`}
              onClick={() => onUpdate({
                ...component,
                data: {
                  ...data,
                  active_tab: index
                }
              })}
            >
              {editingTabIndex === index ? (
                <input
                  type="text"
                  className="px-1 bg-transparent rounded focus:outline-none w-20"
                  value={editTabName}
                  onChange={(e) => setEditTabName(e.target.value)}
                  onBlur={saveTabName}
                  onKeyPress={(e) => e.key === 'Enter' && saveTabName()}
                  autoFocus
                />
              ) : (
                <>
                  <span className={`truncate ${isFullscreen ? 'max-w-[200px]' : 'max-w-[120px]'}`} title={tab.name}>{tab.name}</span>
                  <button 
                    className="p-1 hover:opacity-80"
                    onClick={(e) => {
                      e.stopPropagation();
                      startEditingTab(index);
                    }}
                  >
                    <FaEdit size={10} className={index === data.active_tab ? 'text-white' : 'text-[var(--text-secondary)]'} />
                  </button>
                </>
              )}
              
              {data.tabs.length > 1 && (
                <button
                  className="p-1 hover:opacity-80"
                  onClick={(e) => {
                    e.stopPropagation();
                    setDeleteConfirm({ index, tabName: tab.name });
                  }}
                >
                  <FaTimes size={10} className={index === data.active_tab ? 'text-white' : 'text-[var(--text-secondary)]'} />
                </button>
              )}
            </div>
          ))}
          
          <button
            className="p-1 rounded-md bg-[var(--bg-hover)] hover:bg-[var(--primary)] hover:text-white transition-colors text-sm"
            onClick={addTab}
            title="Add new tab"
          >
            <FaPlus size={12} />
          </button>
        </div>

        {/* Markdown toggle button - always on the right */}
        {data.tabs.length > 0 && (
          <button
            className={`px-3 py-2 rounded-md transition-colors text-sm ml-2 flex items-center justify-center ${
              isMarkdownMode 
                ? 'bg-[var(--bg-hover)] text-[var(--text-primary)]' 
                : 'bg-[var(--bg-hover)] hover:bg-[var(--primary)] hover:text-white'
            }`}
            onClick={() => setIsMarkdownMode(!isMarkdownMode)}
            title={isMarkdownMode ? "Switch to edit mode" : "Preview markdown"}
          >
            {isMarkdownMode ? <FaCode size={14} /> : <FaEye size={14} />}
          </button>
        )}
      </div>
      
      {/* Delete confirmation modal */}
      {deleteConfirm && createPortal(
        <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center z-[10000]">
          <div className="bg-[var(--bg-primary)] p-4 rounded-lg max-w-sm w-full mx-4 max-h-[90vh] overflow-y-auto">
            <p className="mb-4">Are you sure you want to delete "{deleteConfirm.tabName}"?</p>
            <div className="flex justify-end gap-2">
              <button 
                className="px-3 py-1 rounded bg-[var(--bg-hover)] hover:bg-[var(--bg-hover)]"
                onClick={() => setDeleteConfirm(null)}
              >
                Cancel
              </button>
              <button 
                className="px-3 py-1 rounded bg-danger hover:bg-danger hover:scale-105 hover:shadow-lg text-white transition-all duration-200"
                onClick={() => deleteTab(deleteConfirm.index)}
              >
                Delete
              </button>
            </div>
          </div>
        </div>,
        document.body
      )}
      
      {isMarkdownMode ? (
        <div 
          className={`w-full p-4 rounded-lg bg-[var(--bg-primary)] text-[var(--text-primary)] overflow-y-auto ${isFullscreen ? 'h-[70vh]' : 'h-64'}`}
          style={{ 
            fontFamily: 'inherit',
            lineHeight: '1.6'
          }}
        >
          {localContent ? (
            <div 
              className="max-w-none text-[var(--text-primary)]"
              dangerouslySetInnerHTML={{ __html: parseMarkdown(localContent) }}
              style={{
                color: 'var(--text-primary)',
              }}
            />
          ) : (
            <div className="text-[var(--text-secondary)] italic">
              {data.tabs.length ? "Start writing to see markdown preview..." : "Click the + button to create your first tab"}
            </div>
          )}
        </div>
      ) : (
        <textarea
          ref={textareaRef}
          className={`w-full p-4 rounded-lg focus:outline-none focus:ring-1 focus:ring-[var(--primary)] bg-[var(--bg-primary)] text-[var(--text-primary)] ${isFullscreen ? 'h-[70vh]' : 'h-64'}`}
          value={localContent}
          onChange={handleTextChange}
          placeholder={data.tabs.length ? "Start writing..." : "Click the + button to create your first tab"}
        />
      )}
    </>
  );
}